(ns manage.product-table.view
  (:require ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium useGridApiContext]]
            ["src/svg/inform/plus.svg$default" :as Plus]
            ["src/svg/shapes/rectangle/rectangle-split-3x1.svg$default" :as ColumnsIcon]
            [cljs-bean.core :refer [->clj ->js]]
            [common.patterns.loading-view :refer [loading-view]]
            [common.queries :refer [use-query]]
            [common.resource.query.product :as product.q]
            [common.resource.query.product-store :as product-store.q]
            [common.util :refer [format-iso-string spread-obj spread-props]]
            [common.widgets.data-grid :as dg]
            [common.widgets.input :refer [input]]
            [common.widgets.preview-pop-up :refer [preview-pop-up]]
            [manage.product-selector-page.hooks :as ps.hooks]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui create-and-delete-buttons []
  ($ :<>
     ($ Button
        {:on-click #(rf/dispatch [:modal/open {:id :delete-products-modal}])

         :color "error"
         :data-testid "delete-all-products-button"}
        "Delete all products")
     ($ Button
        {:on-click #(rf/dispatch [:ocp/open {:id :product/crud}])
         :variant "contained"
         :startIcon ($ Plus)
         :data-testid "create-product-button"}
        "New product")))

(def Toolbar
  (uix/as-react
   (fn [{:keys [show-create-and-delete?]}]
     (let [api (-> (useGridApiContext) .-current)
           product-store-fields (ps.hooks/use-product-store-fields)
           [_ set-filter-model] (dg/use-filter-model)
           [field set-field] (uix/use-state nil)
           [term set-term] (uix/use-state "")

           confirm-search (uix/use-callback
                           (fn []
                             (when (and field (seq term))
                               (let [filter {(keyword (:kind field)) (:name field)
                                             :field (:name field)
                                             :kind (:kind field)
                                             :operator "match"
                                             :value term}]
                                 (set-filter-model #js {:items #js [(->js filter)]}))))
                           [field term set-filter-model])

           clear-search (uix/use-callback
                         (fn []
                           (set-filter-model #js {:items #js []}))
                         [set-filter-model])]
       ($ dg/grid-toolbar
          {:hide-quick-filter true}
          ($ Stack
             {:direction "row"
              :gap 1}
             ($ Autocomplete
                {:value field
                 :options (into-array product-store-fields)
                 :onChange (fn [_e value]
                             (when (nil? value)
                               (clear-search))
                             (set-field value))
                 :getOptionLabel (fn [option]
                                   (or (:label option) (:xlabel option)))
                 :renderInput (fn [^js props]
                                ($ TextField (spread-props props #js {:placeholder "Search field"})))
                 :data-testid "search-field-input"
                 :sx #js {:minWidth "15rem"}})
             ($ input
                {:value term
                 :onChange #(set-term (-> % .-target .-value))
                 :onKeyPress (fn [e]
                               (when (= 13 (.-charCode e))
                                 (confirm-search)))
                 :disabled (nil? field)
                 :placeholder "Search Term"
                 :data-testid "product-table-filter-input"
                 :sx #js {:width "20rem"}})
             ($ Button
                {:onClick confirm-search
                 :variant "text"
                 :data-testid :product-table-filter-button
                 :sx #js {:width "min-content"}}
                "Search"))
          ($ Stack
             {:direction "row"
              :gap 1}
             ($ Button
                {:endIcon ($ ColumnsIcon)
                 :onClick #(.. api (showPreferences "columns"))}
                "Columns")
             (when show-create-and-delete?
               ($ create-and-delete-buttons))))))))

(defn type->value-fn [type]
  (case type
    "date-time" (fn [value]
                  (some-> value js/Date.))
    identity))

(defn type->renderCell [type]
  (case type
    "boolean" (fn [^js params]
                (if (.-value params)
                  ($ Icon {:color "success"} "check")
                  ($ Icon {:color "error"} "close")))
    ("text" "image" "image-url") (fn [^js params]
                                   (r/as-element
                                    [preview-pop-up (keyword type)
                                     (.-value params)]))
    nil))

(def default-columns
  [#js {:field "sku"
        :headerName "SKU"
        :sortable true
        :width 120
        :valueGetter (fn [_value row]
                       (:sku row))}
   #js {:field "updated-at"
        :headerName "Last modified"
        :sortable true
        :width 180
        :valueGetter (fn [_value row]
                       (some-> (:updated-at row) js/Date.))
        :renderCell (fn [^js row]
                      (some-> (.-value row) format-iso-string))}])

(defn- get-product-store-fields [^js data]
  (->> data ->clj :fields))

(defhook use-product-store-columns []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {product-store-fields :data} (use-query (spread-obj (product-store.q/product-store-query
                                                             {:advertiser-id advertiser-id})
                                                            #js {:select get-product-store-fields}))
        type->mui-column-type (fn [type]
                                (case type
                                  "date-time" "dateTime"
                                  "string"))]
    (some->> product-store-fields
             (map (fn [{:keys [name type sortable]}]
                    (spread-obj
                     #js {:field name
                          :headerName name
                          :type (type->mui-column-type type)
                          :sortable sortable
                          :flex 1
                          :valueGetter (fn [_value row]
                                         (let [value-fn (type->value-fn type)]
                                           (-> (get-in row [:fields (keyword name)])
                                               value-fn)))}
                     (when-let [renderCell (type->renderCell type)]
                       #js {:renderCell renderCell})))))))

(defhook use-product-table-columns []
  (let [product-store-columns (use-product-store-columns)]
    (some->> product-store-columns
             (concat default-columns)
             into-array)))

(defn get-row-id [row] (:id row))

(def get-row-count (comp :total :meta))

(defui products-datagrid [{:keys [on-row-click disabled-products show-create-and-delete?]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        columns (use-product-table-columns)

        initial-visibility-model (uix/use-memo
                                  (fn []
                                    (some->> columns
                                             (map-indexed (fn [idx ^js col]
                                                            [(.-field col) (< idx 5)]))
                                             (into {})
                                             ->js))
                                  [columns])

        is-row-disabled (uix/use-callback
                         (fn [row]
                           (boolean (some #(= (:sku %) (:sku row)) disabled-products)))
                         [disabled-products])

        get-row-class-name (uix/use-callback
                            (fn [^js params]
                              (let [row (.-row params)
                                    disabled? (is-row-disabled row)]
                                (when disabled?
                                  "disabled")))
                            [is-row-disabled])

        row-click-fn (uix/use-callback
                      (fn [^js params]
                        (let [row (.-row params)
                              disabled? (is-row-disabled row)]
                          (when-not disabled?
                            (on-row-click (.-row params)))))
                      [is-row-disabled on-row-click])

        table-query-fn (uix/use-callback
                        (fn [table-models]
                          (product.q/products-query
                           {:advertiser-id advertiser-id}
                           table-models))
                        [advertiser-id])
        server-table-props (dg/use-server-table-props
                            {:table-query-fn table-query-fn
                             :disable-default-transform true
                             :transform-response-fn :items
                             :get-row-count-fn get-row-count
                             :initial-pagination-model #js {:page 0 :pageSize 10}
                             :initial-sort-model #js []})]
    (if-not initial-visibility-model
      ($ loading-view)
      ($ DataGridPremium
         (merge server-table-props
                ;; :key is needed to re-mount the table after switching advertisers,
                ;; such that the column visibility model is initialised correctly
                {:key advertiser-id
                 :data-testid "table-product"
                 :columns columns
                 :getRowId get-row-id
                 :getRowClassName get-row-class-name
                 :onRowClick row-click-fn
                 :disableColumnFilter true
                 :disableRowSelectionOnClick true
                 :slots #js {:toolbar Toolbar}
                 :slotProps #js {:toolbar #js {:show-create-and-delete? show-create-and-delete?}}
                 :initialState #js {:columns #js {:columnVisibilityModel initial-visibility-model}}
                 :sx #js {:maxHeight "70rem"
                          ".MuiDataGrid-row.disabled" #js {:color "text.disabled"}}})))))
